import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { useUnsavedExitConfirm } from "@hooks/useUnsavedExitConfirm";
import {
  UserProductCategorySource,
  UserProductImageSource,
  UserProductUpsertRequestSourceType,
} from "@apis/generated/openapi";
import { LinkInputStep } from "./LinkInputStep";
import { ProductEditor } from "./ProductEditor";
import { SavedProductPicker } from "./SavedProductPicker";
import { useProductForm } from "./useProductForm";
import type {
  CreateProductEntityModalProps,
  CreateProductMode,
  CreateProductStep,
  ProductImageFile,
  ProductImageResponse,
} from "./types";

let savedSlotIdSeq = 0;
const nextSavedSlotUid = () => `saved-slot-${++savedSlotIdSeq}-${Date.now()}`;

/** 给定 defaultMode 时初始 step：manual 直接进表单编辑，其他先进 input 子态 */
const initialStepFor = (mode: CreateProductMode): CreateProductStep => (mode === "manual" ? "editor" : "input");

/**
 * Create Product Entity Modal — Library > Product > Created 入口。
 *
 * 支持三种创建模式（由 CreateMethodDropdown 选择后注入 defaultMode）：
 * - link：粘贴 TikTok / Kalodata 商品链接，调用 fetchParseInfoByLink 自动填充
 * - manual：手动上传图片 + 填写字段
 * - saved：从用户已收藏的 Kalodata 商品中选一个一键填入
 *
 * 编辑态：传入 productId，自动拉详情走更新接口。
 */
export const CreateProductEntityModal = ({
  open,
  onClose,
  onSuccess,
  productId,
  defaultMode = "link",
}: CreateProductEntityModalProps) => {
  const isEditMode = !!productId;
  const [mode, setMode] = useState<CreateProductMode>(isEditMode ? "manual" : defaultMode);
  const [step, setStep] = useState<CreateProductStep>(isEditMode ? "editor" : initialStepFor(defaultMode));

  const {
    form,
    updateForm,
    setForm,
    imageFiles,
    setImageFiles,
    handleFilesChange,
    uploadFile,
    updateCaption,
    submitting,
    parsingLink,
    linkError,
    setLinkError,
    sellingPointsStatus,
    aiAnalyzing,
    needAiAnalyze,
    setSellingPoints,
    addSellingPoint,
    removeSellingPoint,
    parseLink,
    inferAiSellingPoints,
    aiAnalyze,
    submit,
    isDirty,
    captureBaseline,
  } = useProductForm({
    productId,
    enabled: open,
    onSuccess: (detail) => {
      onSuccess?.(detail ?? undefined);
      onClose();
    },
  });

  const { requestClose, exitConfirmModal } = useUnsavedExitConfirm({
    open,
    onClose,
    isDirty,
    shouldGuard: step === "editor",
    blocked: submitting,
  });

  // Modal 打开 / 关闭时按 defaultMode 重置模式与子态
  useEffect(() => {
    if (isEditMode) {
      setMode("manual");
      setStep("editor");
      return;
    }
    setMode(defaultMode);
    setStep(initialStepFor(defaultMode));
  }, [open, isEditMode, defaultMode]);

  const handleParseLink = async (url: string, signal?: AbortSignal) => {
    const ok = await parseLink(url, signal);
    if (ok && !signal?.aborted) {
      setMode("link");
      setStep("editor");
    }
    return ok;
  };

  /** link 解析失败时的兜底：切到手动编辑（节点 3431:22805 失败态设计） */
  const handleSwitchToManual = () => {
    setLinkError(null);
    setMode("manual");
    setStep("editor");
  };

  const handleSavedSelected = (product: any) => {
    const categoryText = [product.priCateName, product.secCateName, product.terCateName].filter(Boolean).join(" > ");
    const cover = product.coverImageUrl;
    const files: ProductImageFile[] = cover
      ? [
          {
            uid: nextSavedSlotUid(),
            name: "main",
            url: cover,
            response: {
              assetId: undefined,
              description: "",
              aiAnalyzed: true, // Kalodata 收藏商品视为已分析（不必再走 AI Analyze）
              source: UserProductImageSource.KALODATA,
            } satisfies ProductImageResponse,
          },
        ]
      : [];

    const nextForm = {
      sourceType: UserProductUpsertRequestSourceType.KALODATA,
      sourceProductId: product.id,
      country: product.country,
      title: product.productTitle ?? product.product_title ?? "",
      category: categoryText,
      categorySource: UserProductCategorySource.LINK,
      sellingPoints: [],
    };
    setForm(nextForm);
    setImageFiles(files);
    captureBaseline({ form: nextForm, imageFiles: files });
    setStep("editor");
  };

  const isFirstStep = step === "input" && mode === "link";

  const renderBody = () => {
    if (isFirstStep) {
      return (
        <LinkInputStep
          parsing={parsingLink}
          error={linkError}
          onParse={handleParseLink}
          onSwitchToManual={handleSwitchToManual}
        />
      );
    }
    if (step === "input" && mode === "saved") {
      return <SavedProductPicker onSelect={handleSavedSelected} />;
    }
    return (
      <ProductEditor
        form={form}
        imageFiles={imageFiles}
        submitting={submitting}
        sellingPointsStatus={sellingPointsStatus}
        aiAnalyzing={aiAnalyzing}
        needAiAnalyze={needAiAnalyze}
        isEditMode={isEditMode}
        showRefresh
        onChangeForm={updateForm}
        onFilesChange={handleFilesChange}
        uploadFile={uploadFile}
        onChangeCaption={updateCaption}
        onChangeSellingPoints={setSellingPoints}
        onAddSellingPoint={addSellingPoint}
        onRemoveSellingPoint={removeSellingPoint}
        onRefreshSellingPoints={inferAiSellingPoints}
        onAiAnalyze={aiAnalyze}
        onCancel={requestClose}
        onSubmit={submit}
      />
    );
  };

  return (
    <>
      <ClipModal
        open={open}
        onCancel={requestClose}
        title={<Trans>Create Product</Trans>}
        width={706}
        mask={{ closable: false }}
        classNames={
          isFirstStep
            ? undefined
            : {
                header: "pb-[12px]! px-[8px]! mb-0!",
              }
        }
      >
        <div className="pt-[8px]">{renderBody()}</div>
      </ClipModal>
      {exitConfirmModal}
    </>
  );
};
